/**
 * Los motivos de cancelación de una póliza.
 *
 * ── LO QUE SE GUARDA Y LO QUE SE LEE SON DOS COSAS ──────────────────────────
 *
 * Lissette, 2026-09-01: *"los motivos de cancelación están solo en inglés, al
 * pasar a español siguen en inglés"*.
 *
 * El inglés estaba puesto A PROPÓSITO, y la razón escrita en su día sigue
 * siendo buena: *"es el vocabulario con el que la agencia ya habla con las
 * aseguradoras, y traducirlo partiría en dos el mismo motivo según quién lo
 * cancelara"*. Una póliza cancelada por una agente en español y otra por una
 * en inglés tendrían textos distintos para lo mismo, y el reporte las contaría
 * como dos motivos.
 *
 * Pero eso vale para el VALOR GUARDADO, no para lo que se lee. Así que:
 *
 *   · lo que se guarda en `cancelReason` sigue siendo el texto en INGLÉS,
 *     idéntico al de siempre — reportes, filtros, exportaciones y las pólizas
 *     ya canceladas no cambian ni una letra;
 *   · lo que se ve en pantalla va en el idioma de quien mira.
 *
 * Es el mismo criterio que ya usan el estatus migratorio y el estado de la
 * póliza: valor estable debajo, etiqueta traducida encima.
 *
 * Vive en su propio módulo —y no dentro de `PoliciesView.jsx`— para poder
 * ejecutarlo en las pruebas sin montar React ni arrastrar MSAL.
 */

/** El catálogo. El VALOR es lo que se guarda; nunca se traduce. */
export const MOTIVOS_DE_CANCELACION = [
  'OTHER AGENT', 'DOCUMENTS', 'MARRIED TO A CLIENT', 'FTR',
  'WORK INSURANCE', 'MOVED', 'MISSING BINDER PAYMENT', 'LIFE CHANGE',
  'DELINQUENT', 'UNKNOWN', 'UNDER A CLIENT POLICY', 'PLAN CHANGE',
  'NOT INTERESTED', 'AUTORENEWED', 'MEDICARE',
  'INCOME TOO HIGH', 'TOO EXPENSIVE', 'MEDICAID', 'TAXES',
];

const EN_ESPANOL = {
  'OTHER AGENT': 'Otro agente',
  DOCUMENTS: 'Documentos',
  'MARRIED TO A CLIENT': 'Se casó con un cliente',
  // FTR no se traduce: es la sigla con la que el Marketplace y las
  // aseguradoras nombran el fallo de reconciliación de impuestos, y la agente
  // la va a ver escrita así en la carta que le llega al cliente.
  FTR: 'FTR (no reconcilió impuestos)',
  'WORK INSURANCE': 'Seguro del trabajo',
  MOVED: 'Se mudó',
  'MISSING BINDER PAYMENT': 'Falta el pago inicial (binder)',
  'LIFE CHANGE': 'Cambio de vida',
  DELINQUENT: 'En mora',
  UNKNOWN: 'Desconocido',
  'UNDER A CLIENT POLICY': 'Bajo la póliza de otro cliente',
  'PLAN CHANGE': 'Cambio de plan',
  'NOT INTERESTED': 'No le interesa',
  AUTORENEWED: 'Renovada automáticamente',
  MEDICARE: 'Medicare',
  'INCOME TOO HIGH': 'Ingresos muy altos',
  'TOO EXPENSIVE': 'Muy caro',
  MEDICAID: 'Medicaid',
  TAXES: 'Impuestos',
};

// ── LOS QUE AÑADE LA AGENCIA ────────────────────────────────────────────────
//
// Lissette, 2026-09-19: *«en la lista de motivo de cancelación, ¿puedes
// colocarlo de manera que puedan escribir más y que se vaya quedando en el
// drop down que ya está?»*.
//
// No se inventa nada: es EXACTAMENTE el mecanismo que ya usan los Marketplaces
// en `PoliciesView.jsx` desde el 2026-08-19, pedido por ella con estas mismas
// palabras —*«poder colocar un personalizado y que se quede allí para las
// próximas»*—. Se guardan en los ajustes de la AGENCIA, así que lo que escribe
// una agente lo encuentra la siguiente.
//
// Se guardan por su TEXTO, no por un id: es lo que ya hay escrito en las
// pólizas viejas, y así un motivo tecleado a mano hace meses coincide solo.
//
// Y NO se traducen: los de fábrica llevan su etiqueta en español porque alguien
// la escribió una por una; uno que teclea la agencia se enseña tal cual lo
// escribió — inventarle una traducción sería peor que no tenerla.
let _motivosDeAgencia = [];

/** Los carga quien lee los ajustes de la agencia al arrancar. */
export function setMotivosDeAgencia(list) {
  _motivosDeAgencia = (Array.isArray(list) ? list : [])
    .map((x) => String(x || '').trim()).filter(Boolean);
}

export function getMotivosDeAgencia() { return _motivosDeAgencia.slice(); }

/**
 * La lista completa: los de fábrica primero, después los de la agencia.
 *
 * Sin repetidos y sin distinguir mayúsculas, como los marketplaces: si alguien
 * escribe "moved" no se crea un gemelo de MOVED que filtraría aparte y
 * contaría aparte en los reportes.
 */
export function todosLosMotivos() {
  const vistos = new Set(MOTIVOS_DE_CANCELACION.map((m) => m.toLowerCase()));
  const extra = [];
  for (const m of _motivosDeAgencia) {
    const clave = m.toLowerCase();
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    extra.push(m);
  }
  return [...MOTIVOS_DE_CANCELACION, ...extra];
}

/** Cómo se LEE un motivo del catálogo. Uno de fuera se enseña tal cual. */
export function etiquetaDeMotivo(valor, lang) {
  const v = String(valor || '');
  if (lang !== 'es') return v;
  return EN_ESPANOL[v] || v;
}

/** El separador entre el motivo del catálogo y la nota escrita a mano. */
const SEPARADOR = ' — ';

/**
 * Parte lo guardado en (motivo del catálogo, nota escrita a mano).
 *
 * Un texto que no empiece por ninguno del catálogo entra ENTERO como nota: los
 * motivos escritos a mano antes de que existiera la lista siguen viéndose
 * igual, que es justo lo que no se puede romper aquí.
 *
 * NO RECORTA, y esa es la segunda mitad del arreglo de los espacios. La
 * pantalla hace el viaje de ida y vuelta —`unirMotivo` al escribir,
 * `partirMotivo` al volver a pintar— en CADA TECLA:
 *
 *     tecleas "hola "  →  unirMotivo   →  se guarda "hola "
 *                      →  partirMotivo →  la caja enseña "hola"   ← aquí
 *                      →  la siguiente letra cae pegada: "holamundo"
 *
 * Quitar el `trim()` de `unirMotivo` no bastó: mientras quedara UNO en el
 * camino, el espacio moría igual. Los dos tienen que dejarlo pasar, y los
 * extremos se limpian solo al GUARDAR (`limpiarMotivo`).
 *
 * Es el inverso EXACTO de `unirMotivo`: corta por el separador ENTERO, en vez
 * de por una expresión regular de "guion con espacios alrededor", que se comía
 * también un espacio escrito a propósito al principio de la nota.
 *
 * (Y ese patrón NO se escribe aquí tal cual: lleva dentro la pareja de
 * caracteres que cierra un bloque de comentario, y cerraría éste a la mitad —
 * la trampa que ya está apuntada en CLAUDE.md.)
 */
export function partirMotivo(guardado) {
  const s = String(guardado || '');
  if (!s) return { motivo: '', nota: '' };
  // `todosLosMotivos()` y no la lista de fábrica: si no, un motivo que añadió
  // la agencia se leería ENTERO como nota, el desplegable saldría vacío al
  // reabrir la póliza y su nota aparecería con el motivo pegado delante.
  //
  // Se busca el más LARGO que encaje. Con "MEDICARE" y un "MEDICARE PART D"
  // de la agencia, quedarse con el primero que coincide dejaría " PART D"
  // como nota — el motivo partido en dos.
  const candidatos = todosLosMotivos().filter((r) => s === r || s.startsWith(`${r}${SEPARADOR}`));
  const hit = candidatos.sort((a, b) => b.length - a.length)[0];
  if (!hit) return { motivo: '', nota: s };
  if (s === hit) return { motivo: hit, nota: '' };
  return { motivo: hit, nota: s.slice(hit.length + SEPARADOR.length) };
}

/**
 * Junta las dos piezas en el texto que se guarda.
 *
 * NO recorta la nota, y esa es la corrección: `setCancelParts` hacía
 * `note.trim()` en CADA TECLA. Al escribir "hola " el espacio del final
 * desaparecía en el acto, así que la siguiente letra caía pegada y salía
 * "holamundo" (Lissette 2026-09-01: *"no acepta espacio, todo lo escribe
 * pegado"*). Los espacios de los extremos se limpian al GUARDAR — ver
 * `limpiarMotivo` —, que es cuando estorban y no mientras se teclea.
 */
export function unirMotivo(motivo, nota) {
  return [String(motivo || ''), String(nota || '')].filter((p) => p !== '').join(SEPARADOR);
}

/** Lo que se guarda de verdad: aquí sí se recortan los extremos. */
export function limpiarMotivo(guardado) {
  const { motivo, nota } = partirMotivo(guardado);
  return unirMotivo(motivo, nota.trim());
}

/**
 * Cómo se LEE un motivo completo, con su nota, en la tarjeta de la póliza.
 * La nota la escribió una persona: se enseña tal cual, sin tocarla.
 */
export function mostrarMotivo(guardado, lang) {
  const { motivo, nota } = partirMotivo(guardado);
  if (!motivo) return nota;
  return unirMotivo(etiquetaDeMotivo(motivo, lang), nota);
}

/**
 * EDITAR Y QUITAR los motivos propios de la agencia (Lissette 2026-10-22: "en
 * la lista de motivos de cancelación poder editar o eliminar lo que se ponga
 * personalizado"). Los de fábrica no se tocan.
 *
 * Se guardan por TEXTO en cada póliza, así que cambiar o quitar uno aquí NO
 * cambia las pólizas que ya lo tienen: lo conservan y se sigue viendo (el
 * desplegable lo ofrece como opción suya).
 */
export function renombrarMotivo(lista, viejo, nuevo) {
  const n = String(nuevo || '').trim();
  if (!n) return { error: 'vacio' };
  if (n.length > 60) return { error: 'largo' };
  const v = String(viejo || '').toLowerCase();
  const choca = [...MOTIVOS_DE_CANCELACION, ...(lista || [])]
    .some((m) => m.toLowerCase() === n.toLowerCase() && m.toLowerCase() !== v);
  if (choca) return { error: 'repetido' };
  return { lista: (lista || []).map((m) => (m.toLowerCase() === v ? n : m)) };
}

export function quitarMotivo(lista, motivo) {
  const v = String(motivo || '').toLowerCase();
  return (lista || []).filter((m) => m.toLowerCase() !== v);
}
